import { Component, OnInit, ViewChild } from '@angular/core'
import { FormControl } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { UnespCoreAuthService, UnespCoreMessageService } from 'src/libs/unesp-core'
import { GestaoUsuario } from 'src/app/models/gestao-usuario'
import { GestaoUsuarioService } from 'src/app/services/gestao-usuario.service'
import { UserLoginService } from 'src/app/services/usuario-login.service'
import { ConfirmDialogModel, ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { DialogGestaoUsuarioComponent } from '../dialog-gestao-usuario/dialog-gestao-usuario.component'
import { DialogBuscarUsuarioComponent } from '../dialog-buscar-usuario/dialog-buscar-usuario.component'
import { DefaultPaginatorComponent } from '../../shared'

@Component({
  selector: 'app-lista-gestao-usuario',
  templateUrl: './lista-gestao-usuario.component.html',
  styleUrls: ['./lista-gestao-usuario.component.scss'],
})
export class ListaGestaoUsuarioComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort
  @ViewChild('paginator') paginator!: DefaultPaginatorComponent

  dataSource: MatTableDataSource<GestaoUsuario> = new MatTableDataSource()
  colunasGrid: string[] = ['username', 'email', 'matricula', 'unidade', 'codigoUnidade', 'papeis', 'action']
  totalElements = 0
  pageSize = 10
  pageIndex = 0
  sortField = 'nome'
  sortDirection = 'asc'

  searchFC = new FormControl('')
  papelFC = new FormControl('')
  unidadeFC = new FormControl('')
  siglaFC = new FormControl('')

  isSuperAdmin = false
  filtrosFixos = false
  acessoPermitido = true

  papeis = [
    { value: '', label: 'Todos' },
    { value: 'CONCURSOADMIN', label: 'Administrador de Concursos' },
    { value: 'EXAMINADOR', label: 'Examinador' },
    { value: 'SG', label: 'SG' },
    { value: 'SEM_PAPEL', label: 'Sem papel' },
  ]

  constructor(
    private gestaoUsuarioService: GestaoUsuarioService,
    private dialog: MatDialog,
    private unespCoreMessageService: UnespCoreMessageService,
    private unespCoreAuthService: UnespCoreAuthService,
    private userLoginService: UserLoginService
  ) {}

  ngOnInit() {
    this.isSuperAdmin = this.unespCoreAuthService.hasPermissionBasedRoles(['SUPERADMIN'])

    if (!this.isSuperAdmin) {
      this.gestaoUsuarioService.verificarAcesso().subscribe(permitido => {
        this.acessoPermitido = permitido
        if (!permitido) return
        const user = this.userLoginService.getUser()
        this.filtrosFixos = true
        this.siglaFC.setValue(user?.sigla || '')
        this.siglaFC.disable()
        this.unidadeFC.disable()
        this.papelFC.setValue('CONCURSOADMIN')
        this.papeis = [
          { value: 'CONCURSOADMIN', label: 'Administrador de Concursos' },
          { value: 'SEM_PAPEL', label: 'Sem papel' },
        ]
        this.listar()
      })
    } else {
      this.listar()
    }
  }

  listar() {
    const currentEmail = this.userLoginService.getUser()?.email || ''
    this.gestaoUsuarioService
      .listar(
        this.searchFC.value || '',
        this.papelFC.getRawValue() || '',
        this.unidadeFC.getRawValue() || '',
        this.siglaFC.getRawValue() || '',
        this.pageIndex,
        this.pageSize,
        this.sortField,
        this.sortDirection
      )
      .subscribe(page => {
        this.dataSource.data = this.isSuperAdmin
          ? page.content
          : page.content.filter((u: GestaoUsuario) => u.email !== currentEmail)
        this.totalElements = this.isSuperAdmin ? page.totalElements : this.dataSource.data.length
      })
  }

  filtrar() {
    this.pageIndex = 0
    this.listar()
  }

  limparFiltros() {
    this.searchFC.setValue('')
    if (this.filtrosFixos) {
      this.papelFC.setValue('CONCURSOADMIN')
    } else {
      this.papelFC.setValue('')
    }
    if (!this.filtrosFixos) {
      this.unidadeFC.setValue('')
      this.siglaFC.setValue('')
    }
    this.filtrar()
  }

  pageChanged(event: any) {
    this.pageIndex = event.pageIndex
    this.pageSize = event.pageSize
    this.listar()
  }

  abrirDialog(usuario?: GestaoUsuario) {
    if (!usuario) {
      this.abrirDialogBuscar()
      return
    }
    const currentUsername = this.userLoginService.getUser()?.email || ''
    const dialogRef = this.dialog.open(DialogGestaoUsuarioComponent, {
      width: '600px',
      maxHeight: '90vh',
      data: { usuario, isSuperAdmin: this.isSuperAdmin, currentUsername },
    })
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.listar()
    })
  }

  abrirDialogBuscar() {
    const codigoUnidadePrefix = this.siglaFC.getRawValue() || ''
    const currentUsername = this.userLoginService.getUser()?.email || ''
    const dialogRef = this.dialog.open(DialogBuscarUsuarioComponent, {
      width: '630px',
      maxHeight: '90vh',
      data: { codigoUnidadePrefix, currentUsername, isSuperAdmin: this.isSuperAdmin },
    })
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.listar()
    })
  }

  removerPapel(usuario: GestaoUsuario, papel: string) {
    if (usuario.email === this.userLoginService.getUser()?.email) {
      this.unespCoreMessageService.showMessageError('Você não pode remover seus próprios papéis.')
      return
    }
    const confirmDialog = new ConfirmDialogModel(
      'Atenção',
      `Confirma a remoção do papel ${papel} do usuário ${usuario.nome}?`
    )
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog })
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.gestaoUsuarioService.removerPapel(usuario.username, papel).subscribe(() => {
          this.unespCoreMessageService.showMessageSuccess(`Papel ${papel} removido com sucesso`)
          this.listar()
        })
      }
    })
  }

  sortChanged(event: any) {
    this.sortField = event.active || 'nome'
    this.sortDirection = event.direction || 'asc'
    this.pageIndex = 0
    this.listar()
  }

  getPapeisNomes(usuario: GestaoUsuario): string {
    return usuario.papeis?.map(p => p.nome).join(', ') || ''
  }

  getUnidadeNome(usuario: GestaoUsuario): string {
    return usuario.unidade?.sigla || ''
  }
}
